import { faqsPl } from "@data/pcb-landing/faq";
import {
  aboutUsCopyPl,
  contactFormPl,
  faqStructuredData,
  iotClusterLinks,
  iotKnowledgeBaseLinks,
  organizationStructuredData,
  serviceStructuredData,
} from "@data/iot-landing/shared";
import {
  Battery,
  Calendar,
  Car,
  CheckCircle,
  CircuitBoard,
  Code,
  Cpu,
  Factory,
  FileText,
  FlaskConical,
  Layers,
  LifeBuoy,
  PenTool,
  Rocket,
  Search,
  ShieldCheck,
  Watch,
} from "lucide-react";

import type { FAQCopy } from "@components/bluetooth-landing/FAQ";
import type { FooterCopy } from "@components/bluetooth-landing/Footer";
import type { HeroCopy } from "@components/bluetooth-landing/Hero";
import type { ProcessCopy } from "@components/bluetooth-landing/Process";
import type { ServicesCopy } from "@components/bluetooth-landing/Services";
import type { TargetAudienceCopy } from "@components/bluetooth-landing/TargetAudience";
import type { ValuePropositionCopy } from "@components/bluetooth-landing/ValueProposition";

export const title =
  "Prototypowanie elektroniki i PCB - od schematu do działającego urządzenia";

export const description =
  "Projektowanie PCB i prototypowanie elektroniki dla firm — budujemy prototyp urządzenia elektronicznego od schematu przez PCB po działający PoC gotowy do testów, certyfikacji CE i produkcji małej serii.";

export const structuredData = [
  organizationStructuredData(description),
  serviceStructuredData(
    "Prototypowanie elektroniki i PCB",
    description,
    "Electronics and PCB prototyping",
  ),
  faqStructuredData(faqsPl),
];

export const heroCopy: HeroCopy = {
  header: "Prototypowanie elektroniki",
  headerTail: "i PCB",
  description:
    "Projektujemy schemat, PCB i firmware — od pierwszego PoC na DevKit po własną płytkę gotową do testów w terenie. STM32, ESP32, Nordic nRF i moduły LTE-M — elektronika, która realnie działa, nie tylko na stole laboratoryjnym.",
  cta: {
    text: "Darmowa konsultacja",
    href: "#kontakt",
  },
  secondaryCta: {
    text: "Poznaj nasz proces",
    href: "#proces",
  },
  benefits: [
    { text: "Schemat, PCB i firmware", Icon: CheckCircle },
    { text: "PoC w kilka tygodni", Icon: CheckCircle },
    { text: "Gotowe pod CE i produkcję", Icon: CheckCircle },
  ],
};

export const targetAudienceCopy: TargetAudienceCopy = {
  header: {
    h2: "Kim są nasi klienci?",
    h3: "Dla kogo jest prototypowanie elektroniki i PCB?",
    p: "W zapytaniach klientów elektronika, firmware i IoT prawie zawsze idą razem. Pracujemy z firmami, które potrzebują działającego urządzenia — nie samego schematu na papierze. Czy któryś z tych scenariuszy pasuje do Twojej sytuacji?",
  },
  points: [
    {
      Icon: Watch,
      title: "Budujesz wearable gotowy pod certyfikację CE",
      description:
        "Opaska, sensor noszony na ciele albo urządzenie medyczne klasy wellness — potrzebujesz PCB zoptymalizowanego pod baterię, obudowę i wymagania rynku europejskiego.",
    },
    {
      Icon: Factory,
      title: "Wdrażasz czujniki przemysłowe lub IoT w produkcji",
      description:
        "Monitorowanie maszyn, magazynów czy linii produkcyjnych — potrzebujesz niezawodnej elektroniki z interfejsami przemysłowymi i firmware gotowego do pracy 24/7.",
    },
    {
      Icon: Car,
      title: "Masz pomysł automotive lub mobility PoC",
      description:
        "Telematyka, czujniki w pojeździe albo urządzenie do testów w terenie — budujemy pierwszy prototyp na sprawdzonych platformach, zanim zainwestujesz w własne PCB seryjne.",
    },
    {
      Icon: Cpu,
      title: "Szukasz partnera od STM32, ESP32 lub Nordic nRF",
      description:
        "Masz koncepcję, ale nie wiesz, jaki mikrokontroler wybrać — dobieramy platformę pod baterię, łączność i koszt produkcji, potem budujemy działający prototyp.",
    },
    {
      Icon: Rocket,
      title: "Masz schemat lub PCB i potrzebujesz dopracowania",
      description:
        "Projekt od poprzedniego dostawcy wymaga poprawek DFM, zasilania albo przygotowania pod montaż — przejmujemy elektronikę i doprowadzamy ją do testów produkcyjnych.",
    },
  ],
  cta: {
    h4: "Brzmi znajomo?",
    text: "Opowiedz nam o swoim urządzeniu — nawet jeśli masz tylko szkic. Pomożemy zaplanować ścieżkę od schematu do działającego prototypu.",
    href: "#kontakt",
    buttonText: "Umów darmową konsultację",
  },
};

export const valuePropositionCopy: ValuePropositionCopy = {
  header: {
    h2: "Nasza specjalizacja",
    h3: "Dlaczego warto zbudować prototyp elektroniki z nami?",
    p: "Łączymy projektowanie PCB, dobór komponentów i firmware w jednym zespole. Nie musisz szukać osobno projektanta hardware i programisty embedded — budujemy kompletny prototyp urządzenia elektronicznego od podstaw.",
    imageAlt: "Prototypowanie elektroniki i PCB — schemat, płytka i firmware",
  },
  values: [
    {
      Icon: CircuitBoard,
      title: "Projektowanie PCB pod realne użycie",
      description:
        "Projektujemy schemat i PCB z myślą o montażu, testowaniu i produkcji małej serii — nie tylko pod demo na biurku. Uwzględniamy DFM, warstwy masy i zasilanie już w pierwszej iteracji.",
    },
    {
      Icon: Battery,
      title: "Elektronika zoptymalizowana pod baterię",
      description:
        "Dobieramy komponenty, topologię zasilania i tryby uśpienia tak, żeby urządzenie realnie pracowało na baterii tygodniami lub miesiącami — nie tylko w specyfikacji datasheetu.",
    },
    {
      Icon: Cpu,
      title: "Sprawdzone platformy: STM32, ESP32, Nordic",
      description:
        "Zaczynamy od DevKitów (np. Nordic Thingy) dla szybkiego PoC, potem przechodzimy na własne PCB pod koszt jednostkowy i wymagania produkcyjne klienta.",
    },
    {
      Icon: ShieldCheck,
      title: "Projektowanie z myślą o certyfikacji CE",
      description:
        "Projektujemy elektronikę z uwzględnieniem EMC, bezpieczeństwa i dokumentacji potrzebnej do CE — szczególnie ważne dla wearable, urządzeń medycznych klasy wellness i produktów B2B.",
    },
    {
      Icon: Layers,
      title: "Elektronika + firmware + aplikacja w jednym zespole",
      description:
        "W leadach klienci prawie zawsze proszą o urządzenie z firmware i panelem lub aplikacją — dlatego prototyp elektroniki budujemy razem z oprogramowaniem, nie jako osobny silos.",
    },
  ],
};

export const servicesCopy: ServicesCopy = {
  header: {
    h2: "Usługi",
    h3: "Prototypowanie elektroniki i projektowanie PCB",
    p: "Od analizy koncepcji po działający prototyp urządzenia elektronicznego gotowy do testów z użytkownikami. Zajmujemy się schematem, PCB, montażem i firmware.",
  },
  services: [
    {
      Icon: Search,
      title: "Analiza koncepcji i dobór komponentów",
      description:
        "Analizujemy wymagania, dobieramy mikrokontroler, czujniki i moduły łączności, szacujemy BOM, pobór prądu i koszt jednostkowy jeszcze przed lutowaniem pierwszej płytki.",
    },
    {
      Icon: PenTool,
      title: "Projektowanie schematu i PCB",
      description:
        "Tworzymy schemat elektryczny i layout PCB — od dwuwarstwowych płytek PoC po bardziej złożone układy z RF, zasilaniem bateryjnym i interfejsami przemysłowymi.",
    },
    {
      Icon: CircuitBoard,
      title: "Prototypowanie i montaż płytek",
      description:
        "Zamawiamy PCB, lutujemy prototypy SMD i THT, integrujemy moduły LTE-M/NB-IoT, czujniki i złącza — gotowe urządzenie do testów w laboratorium i w terenie.",
    },
    {
      Icon: Code,
      title: "Firmware do prototypu",
      description:
        "Piszemy firmware PoC na STM32, ESP32 lub Nordic nRF — odczyt sensorów, komunikacja BLE/Wi-Fi/LTE-M, logika urządzenia i podstawowa diagnostyka.",
    },
    {
      Icon: FlaskConical,
      title: "Testy elektroniki i iteracje",
      description:
        "Testujemy zasilanie, sygnały, EMC na poziomie prototypu i zbieramy wnioski z testów terenowych — kolejna iteracja PCB uwzględnia realne problemy, nie teoretyczne.",
    },
    {
      Icon: ShieldCheck,
      title: "Przygotowanie pod certyfikację CE",
      description:
        "Projektujemy z myślą o wymaganiach EMC i bezpieczeństwa, przygotowujemy dokumentację techniczną i koordynujemy współpracę z laboratoriami certyfikacyjnymi.",
    },
    {
      Icon: Rocket,
      title: "Plan przejścia do produkcji seryjnej",
      description:
        "Przygotowujemy BOM produkcyjny, wybieramy podwykonawcę montażu, optymalizujemy PCB pod koszt i ilość — od prototypu do pierwszej partii seryjnej.",
    },
    {
      Icon: LifeBuoy,
      title: "Wsparcie po zakończeniu prototypu",
      description:
        "Pomagamy iterować kolejne rewizje PCB, dodawać nowe sensory i przygotować urządzenie do kolejnych rund testów lub inwestycji.",
    },
  ],
  cta: {
    h4: "Nie wiesz, od czego zacząć?",
    p: "Opisz nam swój pomysł na urządzenie — nawet jeśli masz tylko wymagania biznesowe. Podpowiemy, jak zaplanować pierwszy prototyp elektroniki.",
    button: {
      text: "Umów darmową konsultację",
      href: "#kontakt",
    },
  },
};

export const processCopy: ProcessCopy = {
  header: {
    h2: "Nasz Proces",
    h3: "Jak powstaje prototyp elektroniki i PCB",
    p: "Przejrzyste etapy, szybkie iteracje i realny sprzęt w rękach już po kilku tygodniach. Zobacz, jak przechodzimy od pomysłu do działającego urządzenia elektronicznego.",
  },
  stepNumber: "Krok",
  steps: [
    {
      Icon: Calendar,
      title: "Darmowe spotkanie online",
      duration: "w kilka godzin",
      description:
        "30 minut rozmowy z inżynierem. Opisujesz urządzenie, a my dajemy pierwszą ocenę wykonalności, dobór platformy i orientacyjną wycenę prototypu.",
    },
    {
      Icon: FileText,
      title: "Warsztaty koncepcyjne",
      duration: "w kilka dni",
      description:
        "Po sesji online otrzymujesz raport z doborem komponentów, szacowanym BOM, harmonogramem i widełkami budżetowymi na pierwszą iterację PCB.",
    },
    {
      Icon: PenTool,
      title: "Podpisanie umowy i NDA",
      duration: "ustalenie harmonogramu",
      description:
        "Ustalamy zakres schematu, PCB, firmware i testów. Podpisujemy NDA — na naszym wzorze lub na dokumencie klienta.",
    },
    {
      Icon: Search,
      title: "Zakup komponentów i PCB",
      duration: "1–2 tygodnie",
      description:
        "Zamawiamy płytki deweloperskie lub produkujemy pierwszą partię PCB, komponenty i moduły potrzebne do prototypu.",
    },
    {
      Icon: CircuitBoard,
      title: "Budowa pierwszej iteracji",
      duration: "min. 3–6 tygodni",
      description:
        "Schemat, layout PCB, montaż i firmware powstają równolegle — żeby jak najszybciej mieć działające urządzenie do testów.",
    },
    {
      Icon: FlaskConical,
      title: "Testy i iteracje PCB",
      duration: "min. 2–4 tygodnie",
      description:
        "Testujemy prototyp w laboratorium i w realnych warunkach, zbieramy wnioski i wprowadzamy poprawki w kolejnej rewizji płytki.",
    },
    {
      Icon: ShieldCheck,
      title: "Przygotowanie pod CE i produkcję",
      duration: "min. 2 tygodnie",
      description:
        "Optymalizujemy PCB pod DFM, przygotowujemy dokumentację techniczną i plan certyfikacji przed produkcją seryjną.",
    },
    {
      Icon: CheckCircle,
      title: "Przekazanie i dalszy rozwój",
      duration: "na stałe",
      description:
        "Otrzymujesz działający prototyp, pliki PCB, BOM i kod źródłowy firmware — albo kontynuujemy współpracę przy kolejnych iteracjach.",
    },
  ],
  cta: {
    href: "#kontakt",
    text: "Zamów prototyp elektroniki",
  },
};

export const aboutUsCopy = aboutUsCopyPl({
  imageAlt: "Zespół Blues Brackets — prototypowanie elektroniki i PCB",
});

export const faqCopy: FAQCopy = {
  header: {
    h2: "FAQ",
    h3: "Najczęściej zadawane pytania o prototypowanie elektroniki i PCB",
  },
  faqs: faqsPl,
};

export const footerCopy: FooterCopy = {
  header: {
    h2: "Budujesz urządzenie elektroniczne?",
    p: "Umów się na darmową konsultację — opowiedz o schemacie, PCB albo prototypie, a zaproponujemy plan od pomysłu do działającego urządzenia.",
  },
  cta: {
    href: "#kontakt",
    text: "Umów darmową konsultację",
    p: "Projektowanie PCB i prototypowanie elektroniki — od schematu po działający prototyp urządzenia gotowy do testów i certyfikacji CE.",
    email: "hello@bluesbrackets.com",
    phone: "+48 535 462 678",
  },
  links: {
    h4: "Na skróty",
    links: [
      { href: "#uslugi", text: "Nasze usługi" },
      { href: "#proces", text: "Proces współpracy" },
      { href: "#faq", text: "FAQ" },
      { href: "/iot", text: "Systemy IoT" },
      ...iotClusterLinks.filter(
        l => l.href !== "/iot" && l.href !== "/prototypowanie-elektroniki-i-pcb",
      ),
    ],
  },
  knowledgeBase: {
    h4: "Baza wiedzy",
    links: [...iotKnowledgeBaseLinks],
  },
  copyright: "bluesBrackets. Wszelkie prawa zastrzeżone.",
  privacyPolicy: "Polityka prywatności",
  privacyPolicyHref: "/policy",
  contactForm: contactFormPl,
};
